import { useState, useEffect } from 'react';
import { toast } from '../components/Toast';
import { listAddresses, createAddress, updateAddress, deleteAddress, setDefaultAddress } from '../api/address.api';
import api from '../api/index';
import { ENDPOINTS } from '../api/endpoints';
import {
    formatPhoneWithPrefix,
    formatApiValidationError,
    isValidKenyanPhone,
    normalizeKenyanPhone,
    KENYA_PHONE_ERROR,
    KENYA_PHONE_HINT,
    KENYA_PHONE_PLACEHOLDER,
} from '../utils/phone';
import { findKenyaCountry } from '../utils/locations';

const MAX_ADDRESSES = 10;

const LABEL_OPTIONS = [
    { value: 'home', label: 'Home', icon: 'ri-home-line' },
    { value: 'office', label: 'Office', icon: 'ri-building-line' },
    { value: 'other', label: 'Other', icon: 'ri-map-pin-line' },
];

const LABEL_COLORS = {
    home: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    office: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    other: 'bg-muted text-foreground/80 dark:bg-muted/20 dark:text-foreground/70',
};

function AddressFormModal({ isOpen, onClose, onSave, editingAddress }) {
    const [formData, setFormData] = useState({
        fullName: '',
        phone: '',
        addressLine1: '',
        addressLine2: '',
        country: '',
        state: '',
        city: '',
        postalCode: '',
        label: 'home',
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // Location data
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    // Fetch countries on mount
    useEffect(() => {
        if (isOpen) {
            fetchCountries();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || editingAddress || formData.country || countries.length === 0) return;
        const kenya = findKenyaCountry(countries);
        if (kenya) {
            setFormData((prev) => (prev.country ? prev : { ...prev, country: kenya._id }));
        }
    }, [isOpen, editingAddress, countries, formData.country]);

    // Populate form when editing
    useEffect(() => {
        if (isOpen && editingAddress) {
            setFormData({
                fullName: editingAddress.fullName || '',
                phone: editingAddress.phone
                    ? formatPhoneWithPrefix(normalizeKenyanPhone(editingAddress.phone))
                    : '+254',
                addressLine1: editingAddress.addressLine1 || '',
                addressLine2: editingAddress.addressLine2 || '',
                country: editingAddress.countryId?._id || editingAddress.countryId || '',
                state: editingAddress.stateId?._id || editingAddress.stateId || '',
                city: editingAddress.cityId?._id || editingAddress.cityId || '',
                postalCode: editingAddress.postalCode || '',
                label: editingAddress.label || 'home',
            });
        } else if (isOpen) {
            setFormData({
                fullName: '',
                phone: '+254',
                addressLine1: '',
                addressLine2: '',
                country: '',
                state: '',
                city: '',
                postalCode: '',
                label: 'home',
            });
        }
        setErrors({});
    }, [isOpen, editingAddress]);

    // Fetch states when country changes
    useEffect(() => {
        if (formData.country) {
            fetchStates(formData.country);
        } else {
            setStates([]);
            setCities([]);
        }
    }, [formData.country]);

    // Fetch cities when state changes
    useEffect(() => {
        if (formData.state) {
            fetchCities(formData.state);
        } else {
            setCities([]);
        }
    }, [formData.state]);

    const fetchCountries = async () => {
        setLoadingCountries(true);
        try {
            const res = await api.get(ENDPOINTS.LOCATIONS.COUNTRIES);
            if (res.data?.isOk && res.data?.data) {
                const list = res.data.data;
                setCountries(list);
                if (!editingAddress) {
                    const kenya = findKenyaCountry(list);
                    if (kenya) {
                        setFormData((prev) => (prev.country ? prev : { ...prev, country: kenya._id }));
                    }
                }
            }
        } catch {
            console.error('Failed to fetch countries');
        } finally {
            setLoadingCountries(false);
        }
    };

    const fetchStates = async (countryId) => {
        setLoadingStates(true);
        setStates([]);
        setCities([]);
        try {
            const res = await api.get(ENDPOINTS.LOCATIONS.STATES(countryId));
            if (res.data?.isOk && res.data?.data) {
                setStates(res.data.data);
            }
        } catch {
            console.error('Failed to fetch states');
        } finally {
            setLoadingStates(false);
        }
    };

    const fetchCities = async (stateId) => {
        setLoadingCities(true);
        setCities([]);
        try {
            const res = await api.get(ENDPOINTS.LOCATIONS.CITIES(stateId));
            if (res.data?.isOk && res.data?.data) {
                setCities(res.data.data);
            }
        } catch {
            console.error('Failed to fetch cities');
        } finally {
            setLoadingCities(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const nextValue = name === 'phone' ? formatPhoneWithPrefix(value) : value;

        // Clear dependent fields on cascading change
        if (name === 'country') {
            setFormData((prev) => ({ ...prev, country: nextValue, state: '', city: '' }));
        } else if (name === 'state') {
            setFormData((prev) => ({ ...prev, state: nextValue, city: '' }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: nextValue }));
        }

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
        if (!formData.phone.trim() || formData.phone.trim() === '+254') {
            newErrors.phone = 'Phone number is required';
        } else if (!isValidKenyanPhone(formData.phone)) {
            newErrors.phone = KENYA_PHONE_ERROR;
        }
        if (!formData.addressLine1.trim()) newErrors.addressLine1 = 'Address line 1 is required';
        if (!formData.country) newErrors.country = 'Country is required';
        if (!formData.state) newErrors.state = 'State/County is required';
        if (!formData.city) newErrors.city = 'City is required';
        if (!formData.postalCode.trim()) {
            newErrors.postalCode = 'Postal code is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error('Please fix the highlighted fields');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                fullName: formData.fullName.trim(),
                phone: normalizeKenyanPhone(formData.phone),
                addressLine1: formData.addressLine1.trim(),
                addressLine2: formData.addressLine2.trim() || undefined,
                countryId: formData.country,
                stateId: formData.state,
                cityId: formData.city,
                postalCode: formData.postalCode.trim(),
                label: formData.label,
            };
            await onSave(payload);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>

            {/* Modal */}
            <div className="relative bg-card rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-card p-5 border-b border-border flex items-center justify-between z-10">
                    <h3 className="text-lg font-bold text-foreground">
                        {editingAddress ? 'Edit Address' : 'Add New Address'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground"
                    >
                        <i className="ri-close-line text-xl"></i>
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    {/* Label */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Address Type
                        </label>
                        <div className="flex gap-3">
                            {LABEL_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => setFormData((prev) => ({ ...prev, label: opt.value }))}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${formData.label === opt.value
                                        ? 'border-primary bg-primary/5 text-primary'
                                        : 'border-border text-muted-foreground hover:border-primary/50'
                                        }`}
                                >
                                    <i className={opt.icon}></i>
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Full Name */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                            Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            name="fullName"
                            type="text"
                            value={formData.fullName}
                            onChange={handleChange}
                            placeholder="Recipient's full name"
                            className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors ${errors.fullName ? 'border-red-500' : 'border-border focus:border-primary'
                                }`}
                        />
                        {errors.fullName && <p className="mt-1 text-xs text-red-500">{errors.fullName}</p>}
                    </div>

                    {/* Phone */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                            Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder={KENYA_PHONE_PLACEHOLDER}
                            className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors ${errors.phone ? 'border-red-500' : 'border-border focus:border-primary'
                                }`}
                        />
                        {errors.phone ? (
                            <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
                        ) : (
                            <p className="mt-1 text-xs text-muted-foreground">{KENYA_PHONE_HINT}</p>
                        )}
                    </div>

                    {/* Address Line 1 */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                            Address Line 1 <span className="text-red-500">*</span>
                        </label>
                        <input
                            name="addressLine1"
                            type="text"
                            value={formData.addressLine1}
                            onChange={handleChange}
                            placeholder="Street address, P.O. box"
                            className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors ${errors.addressLine1 ? 'border-red-500' : 'border-border focus:border-primary'
                                }`}
                        />
                        {errors.addressLine1 && <p className="mt-1 text-xs text-red-500">{errors.addressLine1}</p>}
                    </div>

                    {/* Address Line 2 */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                            Address Line 2
                        </label>
                        <input
                            name="addressLine2"
                            type="text"
                            value={formData.addressLine2}
                            onChange={handleChange}
                            placeholder="Apartment, suite, building, floor (optional)"
                            className="w-full px-4 py-3 border border-border rounded-lg text-sm outline-none focus:border-primary transition-colors"
                        />
                    </div>

                    {/* Country */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                            Country <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            disabled={loadingCountries}
                            className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors cursor-pointer ${errors.country ? 'border-red-500' : 'border-border focus:border-primary'
                                } ${loadingCountries ? 'bg-muted' : ''}`}
                        >
                            <option value="">
                                {loadingCountries ? 'Loading countries...' : 'Select country'}
                            </option>
                            {countries.map((c) => (
                                <option key={c._id} value={c._id}>
                                    {c.countryName}
                                </option>
                            ))}
                        </select>
                        {errors.country && <p className="mt-1 text-xs text-red-500">{errors.country}</p>}
                    </div>

                    {/* State / City Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* State */}
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">
                                State / County <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                                disabled={!formData.country || loadingStates}
                                className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors cursor-pointer ${errors.state ? 'border-red-500' : 'border-border focus:border-primary'
                                    } ${!formData.country || loadingStates ? 'bg-muted' : ''}`}
                            >
                                <option value="">
                                    {loadingStates
                                        ? 'Loading...'
                                        : !formData.country
                                            ? 'Select country first'
                                            : 'Select state/county'}
                                </option>
                                {states.map((s) => (
                                    <option key={s._id} value={s._id}>
                                        {s.stateName}
                                    </option>
                                ))}
                            </select>
                            {errors.state && <p className="mt-1 text-xs text-red-500">{errors.state}</p>}
                        </div>

                        {/* City */}
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">
                                City <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                disabled={!formData.state || loadingCities}
                                className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors cursor-pointer ${errors.city ? 'border-red-500' : 'border-border focus:border-primary'
                                    } ${!formData.state || loadingCities ? 'bg-muted' : ''}`}
                            >
                                <option value="">
                                    {loadingCities
                                        ? 'Loading...'
                                        : !formData.state
                                            ? 'Select state first'
                                            : 'Select city'}
                                </option>
                                {cities.map((c) => (
                                    <option key={c._id} value={c._id}>
                                        {c.cityName}
                                    </option>
                                ))}
                            </select>
                            {errors.city && <p className="mt-1 text-xs text-red-500">{errors.city}</p>}
                        </div>
                    </div>

                    {/* Postal Code */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                            Postal Code <span className="text-red-500">*</span>
                        </label>
                        <input
                            name="postalCode"
                            type="text"
                            value={formData.postalCode}
                            onChange={handleChange}
                            placeholder="e.g., 00100"
                            className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors ${errors.postalCode ? 'border-red-500' : 'border-border focus:border-primary'
                                }`}
                        />
                        {errors.postalCode && <p className="mt-1 text-xs text-red-500">{errors.postalCode}</p>}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-2">
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 py-3 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {saving && (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            )}
                            {saving
                                ? 'Saving…'
                                : editingAddress
                                    ? 'Update Address'
                                    : 'Add Address'}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className="px-6 py-3 border border-border text-muted-foreground rounded-lg text-sm font-medium hover:bg-muted transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function DeleteConfirmModal({ isOpen, onClose, onConfirm, deleting }) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
            <div className="relative bg-card rounded-xl shadow-xl w-full max-w-sm p-6 text-center">
                <div className="w-14 h-14 bg-red-50 dark:bg-red-950/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-delete-bin-line text-2xl text-red-500"></i>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Delete Address</h3>
                <p className="text-muted-foreground text-sm mb-6">
                    Are you sure you want to delete this address? This action cannot be undone.
                </p>
                <div className="flex items-center gap-3">
                    <button
                        onClick={onClose}
                        disabled={deleting}
                        className="flex-1 py-2.5 border border-border text-muted-foreground rounded-lg text-sm font-medium hover:bg-muted transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={deleting}
                        className="flex-1 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {deleting && (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        )}
                        {deleting ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function Addresses() {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingAddressId, setDeletingAddressId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [settingDefault, setSettingDefault] = useState(null);

    useEffect(() => {
        document.title = 'My Addresses | Fone Express';
        fetchAddresses();
    }, []);

    const fetchAddresses = async () => {
        setLoading(true);
        try {
            const res = await listAddresses();
            if (res.data?.isOk && res.data?.data) {
                setAddresses(res.data.data);
            }
        } catch {
            toast.error('Failed to load addresses');
        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = () => {
        if (addresses.length >= MAX_ADDRESSES) {
            toast.error(`You can have a maximum of ${MAX_ADDRESSES} addresses`);
            return;
        }
        setEditingAddress(null);
        setShowFormModal(true);
    };

    const handleEdit = (address) => {
        setEditingAddress(address);
        setShowFormModal(true);
    };

    const handleSave = async (payload) => {
        try {
            if (editingAddress) {
                const res = await updateAddress(editingAddress._id, payload);
                if (res.data?.isOk) {
                    toast.success('Address updated successfully');
                    setShowFormModal(false);
                    fetchAddresses();
                } else {
                    toast.error(formatApiValidationError(res.data, 'Failed to update address'));
                }
            } else {
                const res = await createAddress(payload);
                if (res.data?.isOk) {
                    toast.success('Address added successfully');
                    setShowFormModal(false);
                    fetchAddresses();
                } else {
                    toast.error(formatApiValidationError(res.data, 'Failed to add address'));
                }
            }
        } catch (err) {
            toast.error(formatApiValidationError(err, 'Something went wrong. Please try again.'));
        }
    };

    const handleDeleteClick = (addressId) => {
        setDeletingAddressId(addressId);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingAddressId) return;

        setDeleting(true);
        try {
            const res = await deleteAddress(deletingAddressId);
            if (res.data?.isOk) {
                toast.success('Address deleted successfully');
                setShowDeleteModal(false);
                setDeletingAddressId(null);
                fetchAddresses();
            } else {
                toast.error(res.data?.message || 'Failed to delete address');
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const handleSetDefault = async (addressId) => {
        setSettingDefault(addressId);
        try {
            const res = await setDefaultAddress(addressId);
            if (res.data?.isOk) {
                toast.success('Default address updated');
                fetchAddresses();
            } else {
                toast.error(res.data?.message || 'Failed to set default address');
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setSettingDefault(null);
        }
    };

    const getLocationName = (location) => {
        if (!location) return '';
        if (typeof location === 'string') return location;
        return location.countryName || location.stateName || location.cityName || location.name || '';
    };

    const formatAddress = (addr) => {
        const parts = [
            addr.addressLine1,
            addr.addressLine2,
            getLocationName(addr.cityId),
            getLocationName(addr.stateId),
            addr.postalCode,
            getLocationName(addr.countryId),
        ].filter(Boolean);
        return parts.join(', ');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 border-4 border-border border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-bold text-foreground">My Addresses</h2>
                    <p className="text-sm text-muted-foreground">
                        {addresses.length} of {MAX_ADDRESSES} addresses
                    </p>
                </div>
                <button
                    onClick={handleAddNew}
                    disabled={addresses.length >= MAX_ADDRESSES}
                    className="px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    <i className="ri-add-line"></i>
                    Add New
                </button>
            </div>

            {addresses.length === 0 ? (
                /* Empty State */
                <div className="bg-card rounded-xl shadow-sm border border-border p-12 text-center">
                    <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-map-pin-line text-3xl text-muted-foreground"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">No addresses yet</h3>
                    <p className="text-muted-foreground text-sm mb-6">
                        Add a delivery address to get started with your orders.
                    </p>
                    <button
                        onClick={handleAddNew}
                        className="px-6 py-3 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
                    >
                        <i className="ri-add-line"></i>
                        Add Your First Address
                    </button>
                </div>
            ) : (
                /* Address Grid */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {addresses.map((addr) => (
                        <div
                            key={addr._id}
                            className={`bg-card rounded-xl shadow-sm border overflow-hidden transition-colors ${addr.isDefault ? 'border-primary' : 'border-border'
                                }`}
                        >
                            <div className="p-5">
                                {/* Top row: label + default badge */}
                                <div className="flex items-center gap-2 mb-3">
                                    <span
                                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${LABEL_COLORS[addr.label] || LABEL_COLORS.other
                                            }`}
                                    >
                                        <i
                                            className={
                                                LABEL_OPTIONS.find((o) => o.value === addr.label)?.icon ||
                                                'ri-map-pin-line'
                                            }
                                        ></i>
                                        {(addr.label || 'other').charAt(0).toUpperCase() +
                                            (addr.label || 'other').slice(1)}
                                    </span>
                                    {addr.isDefault && (
                                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                            <i className="ri-check-line"></i>
                                            Default
                                        </span>
                                    )}
                                </div>

                                {/* Name & Phone */}
                                <p className="font-semibold text-foreground text-sm">{addr.fullName}</p>
                                <p className="text-muted-foreground text-sm mt-1">{addr.phone}</p>

                                {/* Full Address */}
                                <p className="text-muted-foreground text-sm mt-2 leading-relaxed">
                                    {formatAddress(addr)}
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="px-5 py-3 border-t border-border bg-muted/20 dark:bg-muted/10 flex items-center gap-3">
                                <button
                                    onClick={() => handleEdit(addr)}
                                    className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
                                >
                                    <i className="ri-pencil-line"></i>
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDeleteClick(addr._id)}
                                    className="text-sm font-medium text-red-500 hover:underline flex items-center gap-1"
                                >
                                    <i className="ri-delete-bin-line"></i>
                                    Delete
                                </button>
                                {!addr.isDefault && (
                                    <button
                                        onClick={() => handleSetDefault(addr._id)}
                                        disabled={settingDefault === addr._id}
                                        className="text-sm font-medium text-muted-foreground hover:text-foreground hover:underline flex items-center gap-1 ml-auto disabled:opacity-60"
                                    >
                                        {settingDefault === addr._id ? (
                                            <div className="w-3.5 h-3.5 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin"></div>
                                        ) : (
                                            <i className="ri-checkbox-circle-line"></i>
                                        )}
                                        Set as Default
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Address Form Modal */}
            <AddressFormModal
                isOpen={showFormModal}
                onClose={() => {
                    setShowFormModal(false);
                    setEditingAddress(null);
                }}
                onSave={handleSave}
                editingAddress={editingAddress}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setDeletingAddressId(null);
                }}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
            />
        </div>
    );
}
